import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { expressMutation, getQueryKey, useExpressQuery } from '../../../../repository/react-query/useExpress';
import { useContext } from 'react';
import { NotificationContext } from '../../../../NotificationContext';

const baseQueryKey = 'processed-fluid-commodity';

/**
 * Hook to fetch all commodities
 */
export const useGetCommodities = ({ queryParams = {}, enabled = true } = {}) => {
    return useQuery({
        queryKey: [baseQueryKey, getQueryKey(queryParams)],
        queryFn: useExpressQuery({ endpoint: '/processed-fluid/commodity', params: queryParams }),
        enabled,
    });
};

/**
 * Hook to fetch a single commodity by type
 * Used to get rapid test standards for a specific commodity type
 */
export const useGetCommodityByType = ({ type, enabled = true } = {}) => {
    const queryParams = { where: { type } };
    return useQuery({
        queryKey: [baseQueryKey, 'by-type', type],
        queryFn: useExpressQuery({ endpoint: '/processed-fluid/commodity', params: queryParams }),
        enabled: enabled && !!type,
        select: (data) => (Array.isArray(data) ? data[0] : data),
    });
};

/**
 * Hook for updating commodity records.
 *
 * @example
 * const { mutate, isLoading } = useMutateCommodity();
 *
 * // Update a commodity
 * mutate({
 *   _id: "12345",
 *   disabled: true,
 * });
 */
export const useMutateCommodity = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id, name, description, disabled, rapid_test_standards }) =>
            expressMutation({
                method: 'patch',
                endpoint: `/processed-fluid/commodity/${_id}`,
                body: {
                    name,
                    description,
                    disabled,
                    rapid_test_standards,
                },
            }),
        onMutate: async (newItem) => {
            // Cancel any ongoing refetches to avoid overwiriting optimistic update
            await queryClient.cancelQueries({ queryKey: [baseQueryKey] });
            const queries = queryClient.getQueryCache().findAll({ queryKey: [baseQueryKey] });

            queries.forEach((query) => {
                const previousData = query.state.data;

                if (Array.isArray(previousData)) {
                    queryClient.setQueryData(
                        query.queryKey,
                        previousData.map((item) => (item._id === newItem._id ? newItem : item))
                    );
                } else if (previousData && typeof previousData === 'object') {
                    queryClient.setQueryData(query.queryKey, newItem);
                }
            });

            return { queries };
        },
        onError: (err, _, ctx) => {
            // Rollback to previous data
            if (ctx?.queries) {
                ctx.queries.forEach((query) => {
                    queryClient.setQueryData(query.queryKey, query.state.data);
                });
            }

            // Show error
            showSnackbar({ severity: 'error', message: 'An error occurred while updating the commodity. Please try again or contact support if the problem persists.' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

/**
 * Hook for creating commodity records.
 *
 * @example
 * const { mutate, isLoading } = useCreateCommodity();
 *
 * // Create a commodity
 * mutate({
 *   name: "Test Commodity",
 *   description: "Test description",
 * });
 */
export const useCreateCommodity = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ name, description, rapid_test_standards }) =>
            expressMutation({
                method: 'post',
                endpoint: '/processed-fluid/commodity',
                body: {
                    name,
                    description,
                    rapid_test_standards,
                },
            }),
        onError: () => {
            showSnackbar({ severity: 'error', message: 'Failed to create commodity' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};

/**
 * Hook for soft deleting commodity records.
 *
 * @example
 * const { mutate, isLoading } = useDeleteCommodity();
 *
 * // Delete a commodity
 * mutate({
 *   _id
 * });
 */
export const useDeleteCommodity = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id }) =>
            expressMutation({
                method: 'delete',
                endpoint: `/processed-fluid/commodity/${_id}`,
            }),
        onError: (err) => {
            const res = err?.response?.data;
            showSnackbar({ severity: 'error', message: res?.message ?? 'Failed to delete commodity' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
        },
    });
};
